// React and hooks
import React, { useEffect, useState, useRef } from 'react';

// Routing
import { NavLink } from 'react-router-dom';

// UI components
import { Row, Col, Dropdown, Button } from 'react-bootstrap';
import HtmlHead from 'components/html-head/HtmlHead';
import CsLineIcons from 'cs-line-icons/CsLineIcons';

// Date utilities
import moment from 'moment';

// Utilities
import { handlerErrors } from 'utils/handleErrors';

// Custom components
import EmptyPage from 'components/emptyStates/EmptyPage';
import EmptyFilterResult from 'components/emptyStates/EmptyFilterResult';
import ConnectionError from 'components/emptyStates/ConnectionError';
import CardStatement from './components/CardStatement';
import LoadingPulse from '../../../components/loading/LoadingPulse';
import LoadingCar from '../../../components/loading/LoadingCar';
import StatementsCSV from '../../../components/reactCsv/StatementsCSV';
import PaginateLocalStates from '../../../components/PaginateLocalStates';
import RainCalendar from '../../../components/calendars/RainCalendar';
import ProvisionalUserAlert from '../../../components/alerts/UserAlert';

// API services
import { getStatementsHistory, getStatementsFiltered, getChargeConcepts } from '../../../api/Api-user';

const { FormatMoney } = require('format-money-js');

const fm = new FormatMoney({
  decimals: 0, // No decimales
});

const Statements = () => {
  const title = 'Movimientos';
  const description = 'Movimientos Plataforma Autopaquete';
  // -Local States
  const [statements, setStatements] = useState(null);
  const [totalPages, setTotalPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [changePage, setChangePage] = useState(1); // para la peticion
  const [changeItemsNum, setChangeItemsNum] = useState(10); // para la peticion
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasConnectionIssue, setHasConnectionIssue] = useState(false);
  const [emptyHistory, setEmptyHistory] = useState(null);
  // States for filters
  const [conceptName, setConceptName] = useState('');
  const [filteredMode, setFilteredMode] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectStartDate, setSelectStartDate] = useState({ date: undefined });
  const [selectEndDate, setSelectEndDate] = useState({ date: undefined });
  const [filteredSuccess, setFilteredSuccess] = useState('');
  const [conceptList, setConceptList] = useState([]);
  // - Use references to do scroll Up
  const startRef = useRef(null);
  const filterInitialValues = () => {
    setConceptName('');
    setSelectEndDate({ date: undefined });
    setSelectStartDate({ date: undefined });
    setStartDate('');
    setEndDate('');
    setFilteredSuccess(''); // para eliminar el mensaje "Resultado del Filtro..."
  };
  const settingPage = (limit) => {
    // cambia el largo de la pag y se cambia a la primera pag
    // pero debe concervar en estado, en modo filtrado o no
    setChangePage(1);
    setChangeItemsNum(limit);
  };
  const handlerGetStatements = async () => {
    if (statements) {
      setIsLoading(true);
    }
    try {
      const response = await getStatementsHistory(changePage, changeItemsNum);
      // console.log('response', response);
      if (response.statement !== undefined) {
        setStatements(response.statement);
        setCurrentPage(response.current_page); // para actualizar la vista
        setPageSize(response.page_size); // para actualizar la vista
        setTotalItems(response.total_items);
        setTotalPages(response.total_pages);
        if (startRef.current) {
          startRef.current.scrollIntoView({ behavior: 'smooth' });
        }
        if (response.statement.length === 0) {
          setEmptyHistory(statements || true); // para mostrar mensaje de "No tienes historial"
        }
      }
    } catch (error) {
      setStatements(statements || []);
      setHasConnectionIssue(true); // only show component if this is true and history list is empty
      handlerErrors(error);
    } finally {
      setIsLoading(false);
    }
  };
  const handlerGetFilteredStatements = async () => {
    if (statements) {
      setIsLoading(true);
    }
    try {
      const response = await getStatementsFiltered(changePage, changeItemsNum, conceptName, startDate, endDate);
      // console.log('getStatementsFiltered response', response);
      if (response.statement !== undefined) {
        setEmptyHistory(false); // hide empty component when applying filters
        setStatements(response.statement);
        setCurrentPage(response.current_page); // para actualizar la vista
        setPageSize(response.page_size); // para actualizar la vista
        setTotalItems(response.total_items);
        setTotalPages(response.total_pages);
        setFilteredSuccess(
          `Resultados del filtro: ${conceptName} ${startDate ? ` del ${moment(startDate).format('DD-MM-YY')}` : ''} ${
            endDate ? `al ${moment(endDate).format('DD-MM-YY')}` : ''
          }`
        );
        if (startRef.current) {
          startRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }
    } catch (error) {
      setHasConnectionIssue(true); // only show component if this is true and history list is empty
      setStatements(statements || []);
      handlerErrors(error);
    } finally {
      setIsLoading(false);
      // setFilteredSuccess(true);
      setFilteredMode(true); // respuesta exitosa o no es necesario para mantener el modo filtrando y que aparezca el btn regresar
    }
  };
  const returnInitialMode = () => {
    // handlerGetFilteredHistory() o  handlerGetShippingHistory() activan el loader
    // setFilterBy('envío'); // ejecuta el useEffect que prepara los estados para el filtrado
    setFilteredMode(false);
    filterInitialValues();
    if (changePage > 1) {
      setChangePage(1); // ejecutara el useEffect que decide que fn ejecutar pero con la pag 1
    } else {
      // si changePage es 1 no ejecuta el cambio de estado ni el useEffect es necesario llamar a la funcion
      handlerGetStatements();
    }
  };
  useEffect(() => {
    if (filteredMode) {
      handlerGetFilteredStatements();
    } else {
      handlerGetStatements();
    }
  }, [changePage, changeItemsNum]);
  useEffect(() => {
    getChargeConcepts()
      .then((response) => {
        // console.log('concept list res:', response);
        setConceptList(response.list);
      })
      .catch(() => {
        setConceptList(['DHL', 'ESTAFETA', 'JYT', 'ESTAFETA']);
      });
  }, []);
  return (
    <>
      {statements === null && <LoadingCar />}
      {isLoading && <LoadingPulse />}
      <div id="pageContainer">
        <HtmlHead title={title} description={description} />
        <div className="page-title-container" ref={startRef}>
          <Row className="g-0">
            {/* Title Start */}
            <Col className="col-auto mb-3 mb-sm-0 me-auto">
              <NavLink className="muted-link pb-1 d-inline-block hidden breadcrumb-back" to="/">
                <CsLineIcons icon="chevron-left" size="13" />
                <span className="align-middle text-small ms-1">Inicio</span>
              </NavLink>
              <h1 className="mb-0 pb-0 display-4" id="title">
                {title}
              </h1>
            </Col>
            {/* Title End */}
          </Row>
        </div>
        <div style={{ minHeight: '50vh' }} id="cardContainer">
          {/* Elements for Filtering Start */}
          <Row className="g-0 mt-sm-5 d-flex align-items-end bg-infow">
            {/* Filters Start */}
            <Col xs="auto" xl="9" className="bg-warningw">
              <Row className="g-0">
                <Col xs="auto" lg="7" className="mb-2 pe-2 bg-alternatew" style={{ minWidth: '275px' }}>
                  <Dropdown className="d-inline-block">
                    <Dropdown.Toggle variant="foreground-alternate" className="shadow d-flex align-items-center justify-content-between sw-35 sw-lg-40">
                      {conceptName || 'Concepto'}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="shadow sw-auto" style={{ minHeight: '100px', maxHeight: '350px', overflowY: 'scroll', fontFamily: 'Montserrat' }}>
                      <Dropdown.Item onClick={() => setConceptName('')}>Ninguno</Dropdown.Item>
                      {conceptList?.map((concept) => (
                        <Dropdown.Item key={concept} onClick={() => setConceptName(concept.name_concept)}>
                          {concept.name_concept}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </Col>
                {/* Calendars Start */}
                <Col xs="12" sm="auto" className="mb-2 mb-xl-0">
                  <Row className="g-0 me-sm-2">
                    <Col xs="6" className="pe-2">
                      <RainCalendar
                        selectDate={selectStartDate}
                        setSelectDate={setSelectStartDate}
                        setState={setStartDate}
                        state={startDate}
                        holder="Fecha Inicial"
                        width="100px"
                      />
                    </Col>
                    <Col xs="6">
                      <RainCalendar selectDate={selectEndDate} setSelectDate={setSelectEndDate} setState={setEndDate} holder="Fecha Final" width="100px" />
                    </Col>
                  </Row>
                </Col>
                {/* Calendars End */}
                <Col xs="12" md="auto">
                  <Row className="g-0">
                    <Col xs="auto" className="pe-2">
                      <Button
                        variant="primary"
                        style={{ height: '36px' }}
                        className="w-100"
                        onClick={() => {
                          if (changePage > 1) {
                            // la primera vez el estado filteredMode es falso
                            setFilteredMode(true); // para que el useEffect funcione bien
                            setChangePage(1); // ejecutara el useEffect que decide que fn ejecutar pero con la pag 1
                          } else {
                            handlerGetFilteredStatements();
                          }
                        }}
                      >
                        <span>Buscar</span>
                      </Button>
                    </Col>
                    {filteredMode && (
                      <Col xs="auto">
                        <Button variant="outline-primary" style={{ height: '36px' }} className="w-100" onClick={returnInitialMode}>
                          <span>Regresar</span>
                        </Button>
                      </Col>
                    )}
                  </Row>
                </Col>
              </Row>
            </Col>
            {/* Filters End */}
            <Col xs="12" xl="3" className="mt-5 mt-lg-0 d-flex justify-content-end align-items-end bg-secondaryw">
              {/* Excel Buttons  */}
              {statements?.length > 0 && <StatementsCSV statements={statements} crmId="" />}
              {/* Dropdown  */}
              <Dropdown align={{ xs: 'end' }} className={`d-inline-block ms-2 ${statements?.length === 0 && 'd-none'}`}>
                <Dropdown.Toggle variant="foreground-alternate" className="shadow sw-20">
                  {`${pageSize} Movimientos`}
                </Dropdown.Toggle>
                <Dropdown.Menu className="shadow dropdown-menu-end">
                  <Dropdown.Item className={`${pageSize === 10 && 'd-none'}`} onClick={() => settingPage(10)}>
                    10 Movimientos
                  </Dropdown.Item>
                  <Dropdown.Item className={`${pageSize === 20 && 'd-none'}`} onClick={() => settingPage(20)}>
                    20 Movimientos
                  </Dropdown.Item>
                  <Dropdown.Item className={`${pageSize === 50 && 'd-none'}`} onClick={() => settingPage(50)}>
                    50 Movimientos
                  </Dropdown.Item>
                  <Dropdown.Item className={`${pageSize === 100 && 'd-none'}`} onClick={() => settingPage(100)}>
                    100 Movimientos
                  </Dropdown.Item>
                  <Dropdown.Item className={`${pageSize === 100 && 'd-none'}`} onClick={() => settingPage(200)}>
                    200 Movimientos
                  </Dropdown.Item>
                  <Dropdown.Item className={`${pageSize === 100 && 'd-none'}`} onClick={() => settingPage(300)}>
                    300 Movimientos
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              {/* <div className={`${statements?.length === 0 && 'invisible'} text-muted my-3 d-lg-flex`}>{`Página ${currentPage}`}</div> */}
            </Col>
          </Row>
          {/* Elements for Filtering End */}
          {/* List Header Start */}
          <hr className="bg-muted mb-3 mt-3 mt-xl-4" />
          {/* List Header Start */}
          {filteredSuccess && <div className="text-muted mb-3">{filteredSuccess}</div>}
          <div className="d-flex justify-content-between mb-3 text-muted ">
            <span className={`${hasConnectionIssue ? 'invisible' : 'visible'}`}>
              Total: <strong>{fm.from(totalItems)}</strong> moviminetos
            </span>
            <span className={`d-block ${statements?.length < 2 && 'd-none'}`}>
              Página <strong>{currentPage}</strong>
            </span>
          </div>
          <ProvisionalUserAlert title="Historial de movimientos" />
          <Row className={`g-0 h-100 align-content-center d-none d-lg-flex ps-5 pe-5 mb-2 custom-sort ${statements?.length === 0 ? 'invisible' : 'visible'}`}>
            <Col md="3" className="d-flex flex-column mb-lg-0 pe-3 d-flex">
              <div className="text-muted text-small cursor-pointer">CONCEPTO</div>
            </Col>
            <Col md="2" className="d-flex flex-column pe-1 justify-content-center align-items-center">
              <div className="text-muted text-small cursor-pointer">FECHA MOVIMIENTO</div>
            </Col>
            <Col md="2" className="d-flex flex-column pe-1 justify-content-center">
              <div className="text-muted text-small cursor-pointeR">REFERENCIA</div>
            </Col>
            <Col md="3" className="d-flex flex-column pe-1 justify-content-center ps-4">
              <div className="text-muted text-small cursor-pointeR">REFERENCIA ADICIONAL</div>
            </Col>
            <Col md="1" className="d-flex flex-column pe-1 justify-content-center">
              <div className="text-muted text-small cursor-pointer">MONTO</div>
            </Col>
            <Col md="1" className="d-flex flex-column pe-1 justify-content-center text-end">
              <div className="text-muted text-small cursor-pointer">SALDO</div>
            </Col>
          </Row>
          {/* List Header End */}
          {/* List Items Start */}
          {statements?.length === 0 && hasConnectionIssue && <ConnectionError refreshBtn />}
          {emptyHistory && (
            <EmptyPage
              text="Aún no hay movimientos registrados en tu historial. Una vez que realices transacciones o envíos, podrás ver el resumen aquí."
              showAnimation
            />
          )}
          {statements?.length === 0 && filteredSuccess && <EmptyFilterResult />}
          {statements?.length > 0 && (
            <>
              {/* List statements Items Start */}
              {statements?.map((statement) => (
                <CardStatement key={statement.created_id} statement={statement} />
              ))}

              {/* List statements Items End */}
              {/* Pagination Start */}
              <div className={`d-flex flex-column justify-content-center align-items-center mt-5 mx-auto ${totalPages < 2 && 'd-none'}`}>
                <PaginateLocalStates currentPage={currentPage} totalPages={totalPages} setCurrentPage={setChangePage} />
              </div>
              {/* Pagination End */}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Statements;
